import TaskCancelModal from '@/components/TaskCancelModal';
import {
  BellOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { history, useAccess } from '@umijs/max';
import {
  Badge,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { BackendTaskState } from '@/constants/taskProgress';
import RedoApprovalModal from '@/pages/report/components/RedoApprovalModal';
import {
  getPendingReviewCount,
  getPendingTaskCount,
  getTaskList,
  queryReportList,
} from '@/services/ant-design-pro/api';
import { formatTimestampToFullChinese } from '@/utils/dateUtils';
import {
  CARD_BADGE_COLORS,
  getCardDisplayStatus,
  isPendingSupplementWithdrawApproval,
  TaskAssignmentCardStatus,
} from '@/utils/taskCardDisplayStatus';
import './index.less';

const isRedoApprovalPending = (record: any) =>
  Number(record?.state) === BackendTaskState.PENDING_REDO_APPROVAL;

type TodoTabKey = 'task' | 'review';

const parseCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const MyTodo: React.FC = () => {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TodoTabKey>('task');
  const [taskCount, setTaskCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);

  const canAccessTask = !!access.canAccessTaskMyTask;
  const canAccessReview = !!access.canAccessReportReview;
  const visible = canAccessTask || canAccessReview;

  const totalCount =
    (canAccessTask ? taskCount : 0) + (canAccessReview ? reviewCount : 0);

  const fetchCounts = useCallback(async () => {
    if (!visible) return;
    setCountLoading(true);
    try {
      const tasks: Array<Promise<void>> = [];

      if (canAccessTask) {
        tasks.push(
          getPendingTaskCount()
            .then((res) => {
              setTaskCount(parseCount(res?.result));
            })
            .catch(() => {
              setTaskCount(0);
            }),
        );
      }

      if (canAccessReview) {
        tasks.push(
          getPendingReviewCount()
            .then((res) => {
              setReviewCount(parseCount(res?.result));
            })
            .catch(() => {
              setReviewCount(0);
            }),
        );
      }

      await Promise.all(tasks);
    } finally {
      setCountLoading(false);
    }
  }, [visible, canAccessTask, canAccessReview]);

  useEffect(() => {
    fetchCounts();
    const timer = window.setInterval(fetchCounts, 60_000);
    const onFocus = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCounts]);

  useEffect(() => {
    if (!open) return;
    if (canAccessTask) {
      setActiveTab('task');
    } else if (canAccessReview) {
      setActiveTab('review');
    }
  }, [open, canAccessTask, canAccessReview]);

  const handleOpen = () => {
    setOpen(true);
    fetchCounts();
  };

  const handleClose = () => {
    setOpen(false);
    fetchCounts();
  };

  if (!visible) {
    return null;
  }

  const tabItems = [
    canAccessTask
      ? {
          key: 'task' as const,
          label: (
            <span className="my-todo-tab-label">
              我的任务
              <Badge count={taskCount} size="small" overflowCount={99} />
            </span>
          ),
          children: (
            <PendingTaskPanel
              open={open && activeTab === 'task'}
              onHandled={() => {
                handleClose();
              }}
            />
          ),
        }
      : null,
    canAccessReview
      ? {
          key: 'review' as const,
          label: (
            <span className="my-todo-tab-label">
              我的审核
              <Badge count={reviewCount} size="small" overflowCount={99} />
            </span>
          ),
          children: (
            <PendingReviewPanel
              open={open && activeTab === 'review'}
              onHandled={() => {
                handleClose();
              }}
              onRefreshCounts={fetchCounts}
            />
          ),
        }
      : null,
  ].filter(Boolean) as Array<{
    key: TodoTabKey;
    label: React.ReactNode;
    children: React.ReactNode;
  }>;

  return (
    <>
      <button
        type="button"
        className="my-todo-trigger"
        onClick={handleOpen}
        aria-label="我的待办"
      >
        <BellOutlined className="my-todo-trigger-icon" />
        <span className="my-todo-trigger-text">我的待办</span>
        <Badge
          count={countLoading && totalCount === 0 ? 0 : totalCount}
          size="small"
          overflowCount={99}
          className="my-todo-trigger-badge"
        />
      </button>

      <Modal
        title="我的待办"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={1280}
        destroyOnClose
        className="my-todo-modal"
        styles={{ body: { paddingTop: 8 } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TodoTabKey)}
          items={tabItems}
        />
      </Modal>
    </>
  );
};

type PanelProps = {
  open: boolean;
  onHandled: () => void;
  onRefreshCounts?: () => void;
};

/** 搜索条件预留区：后续按产品补充字段 */
type TaskSearchValues = {
  taskName?: string;
  reportNumber?: string;
};

type ReviewSearchValues = {
  taskName?: string;
  estimateReportCode?: string;
};

const PendingTaskPanel: React.FC<PanelProps> = ({ open, onHandled }) => {
  const [form] = Form.useForm<TaskSearchValues>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<TaskSearchValues>({});

  const fetchList = useCallback(
    async (nextPage = page, nextPageSize = pageSize, nextFilters = filters) => {
      setLoading(true);
      try {
        const response = await getTaskList({
          page: nextPage,
          pageSize: nextPageSize,
          status: 'PENDING_HANDLE',
          taskName: nextFilters.taskName || undefined,
          estimateReportCode: nextFilters.reportNumber || undefined,
        });
        if (response.success && response.result) {
          const list = (response.result.list || []).map((item: any) => {
            const display = getCardDisplayStatus(item);
            return {
              ...item,
              key: String(item.taskId ?? ''),
              taskNumber: item.taskId != null ? String(item.taskId) : '',
              reportNumber: item.estimateReportCode || '',
              taskName: item.name || '',
              taskStatus: display.badgeLabel,
              badgeEnum: display.badgeEnum,
            };
          });
          setData(list);
          setTotal(response.result.total || 0);
        } else {
          setData([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('获取待办任务失败:', error);
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, filters],
  );

  useEffect(() => {
    if (!open) return;
    fetchList(page, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, pageSize, filters]);

  const handleSearch = async () => {
    const values = await form.validateFields();
    setPage(1);
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPage(1);
    setFilters({});
  };

  const handleProcess = (record: any) => {
    if (!record?.taskId) return;
    const canEdit =
      record.isCurrentOperator &&
      !isPendingSupplementWithdrawApproval(record) &&
      (record.badgeEnum === TaskAssignmentCardStatus.PENDING ||
        record.badgeEnum ===
          TaskAssignmentCardStatus.PENDING_TOTAL_PRICE_INPUT ||
        record.badgeEnum === TaskAssignmentCardStatus.PRICE_REJECTED ||
        record.badgeEnum === TaskAssignmentCardStatus.REPORT_REJECTED ||
        record.badgeEnum === TaskAssignmentCardStatus.REPORT_TO_SUBMIT);
    onHandled();
    const base = record.multiProperty === true ? '/task/multiProperty' : '/task/create';
    history.push(
      `${base}?mode=${canEdit ? 'edit' : 'view'}&taskId=${record.taskId}&from=myTask`,
    );
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 320,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
    },
    {
      title: '任务状态',
      dataIndex: 'taskStatus',
      key: 'taskStatus',
      width: 120,
      render: (_: unknown, record: any) => {
        const color =
          record.badgeEnum != null
            ? CARD_BADGE_COLORS[record.badgeEnum as TaskAssignmentCardStatus]
            : 'default';
        return <Tag color={color || 'default'}>{record.taskStatus || '-'}</Tag>;
      },
    },
    {
      title: '报告编号',
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 320,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
    },
    {
      title: '当前操作人',
      dataIndex: 'currentOperatorName',
      key: 'currentOperatorName',
      width: 110,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      render: (value: unknown) =>
        formatTimestampToFullChinese(value as any) || '-',
    },
    {
      title: '任务编号',
      dataIndex: 'taskNumber',
      key: 'taskNumber',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 80,
      render: (_: unknown, record: any) => (
        <a onClick={() => handleProcess(record)}>处理</a>
      ),
    },
  ];

  return (
    <div className="my-todo-panel">
      {/* 搜索条件预留：后续可继续追加筛选项 */}
      <Form
        form={form}
        layout="inline"
        className="my-todo-search"
        onFinish={handleSearch}
      >
        <Form.Item name="taskName">
          <Input
            allowClear
            placeholder="请输入任务名称"
            style={{ width: 180 }}
          />
        </Form.Item>
        <Form.Item name="reportNumber">
          <Input
            allowClear
            placeholder="请输入报告编号"
            style={{ width: 200 }}
          />
        </Form.Item>
        {/* TODO: 后续补充更多搜索条件 */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              查询
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1240 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

const PendingReviewPanel: React.FC<PanelProps> = ({
  open,
  onHandled,
  onRefreshCounts,
}) => {
  const [cancelApplicationId, setCancelApplicationId] = useState<string>();
  const [cancelTaskId, setCancelTaskId] = useState<string | number>();
  const [form] = Form.useForm<ReviewSearchValues>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<ReviewSearchValues>({});
  const [redoApprovalOpen, setRedoApprovalOpen] = useState(false);
  const [redoRecord, setRedoRecord] = useState<any>(null);

  const fetchList = useCallback(
    async (nextPage = page, nextPageSize = pageSize, nextFilters = filters) => {
      setLoading(true);
      try {
        const response = await queryReportList({ approvalType: 'ALL',
          page: nextPage,
          pageSize: nextPageSize,
          processStatus: 'PENDING',
          taskName: nextFilters.taskName || undefined,
          estimateReportCode: nextFilters.estimateReportCode || undefined,
        });
        if (response.success && response.result) {
          setData(
            (response.result.list || []).map((item: any, index: number) => ({
              ...item,
              key: item.rowKey || String(item.taskId ?? item.id ?? index),
            })),
          );
          setTotal(response.result.total || 0);
        } else {
          setData([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('获取待办审核失败:', error);
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, filters],
  );

  useEffect(() => {
    if (!open) return;
    fetchList(page, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, pageSize, filters]);

  useEffect(() => {
    if (open) return;
    setRedoApprovalOpen(false);
    setRedoRecord(null);
  }, [open]);

  const handleSearch = async () => {
    const values = await form.validateFields();
    setPage(1);
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPage(1);
    setFilters({});
  };

  const handleReview = (record: any) => {
    if (!record?.taskId) return;
    if (record.approvalType === 'TASK_CANCEL') { setCancelApplicationId(record.applicationId); setCancelTaskId(record.taskId); return; }
    if (isRedoApprovalPending(record)) {
      setRedoRecord(record);
      setRedoApprovalOpen(true);
      return;
    }
    const base = record.multiProperty === true ? '/task/multiPropertyReview' : '/task/detail';
    history.push(
      `${base}?id=${record.taskId}&mode=review&from=reportReview`,
    );
    onHandled();
  };

  const handleRedoApprovalSuccess = () => {
    fetchList(page, pageSize, filters);
    onRefreshCounts?.();
  };

  const columns = [
    {
      title: '电子报告提取码',
      dataIndex: 'electronicReportCode',
      key: 'electronicReportCode',
      width: 180,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (text: string) => text || '-',
    },
    {
      title: '报告编号',
      dataIndex: 'estimateReportCode',
      key: 'estimateReportCode',
      width: 200,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (text: string) => text || '-',
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 240,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
    },
    {
      title: '状态',
      dataIndex: 'stateName',
      key: 'stateName',
      width: 120,
      render: (text: string) => (text ? <Tag color="blue">{text}</Tag> : '-'),
    },
    {
      title: '操作人',
      dataIndex: 'currentOperatorName',
      key: 'currentOperatorName',
      width: 110,
      render: (text: string) => text || '-',
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      render: (value: unknown) =>
        formatTimestampToFullChinese(value as any) || '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 80,
      render: (_: unknown, record: any) => (
        <a onClick={() => handleReview(record)}>处理</a>
      ),
    },
  ];

  return (
    <div className="my-todo-panel">
      {/* 搜索条件预留：后续可继续追加筛选项 */}
      <Form
        form={form}
        layout="inline"
        className="my-todo-search"
        onFinish={handleSearch}
      >
        <Form.Item name="taskName">
          <Input
            allowClear
            placeholder="请输入任务名称"
            style={{ width: 180 }}
          />
        </Form.Item>
        <Form.Item name="estimateReportCode">
          <Input
            allowClear
            placeholder="请输入报告编号"
            style={{ width: 200 }}
          />
        </Form.Item>
        {/* TODO: 后续补充更多搜索条件 */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              查询
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1240 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
      <TaskCancelModal taskId={cancelTaskId} applicationId={cancelApplicationId} onClose={() => setCancelTaskId(undefined)}
        onSuccess={() => { fetchList(page, pageSize, filters); onHandled(); }} />
      <RedoApprovalModal
        open={redoApprovalOpen}
        record={redoRecord}
        zIndex={1100}
        onCancel={() => {
          setRedoApprovalOpen(false);
          setRedoRecord(null);
        }}
        onSuccess={handleRedoApprovalSuccess}
      />
    </div>
  );
};

export default MyTodo;
